import { Link2, Lock } from "lucide-react";
import { useId, useState } from "react";

import { Button } from "@/components/button";

import { CopyEmailButton } from "./CopyEmailButton";
import { getShareTarget } from "./link-meta";

type AssetLinkInputProps = {
  error?: string;
  isWhitelabel: boolean;
  /** Returns whether the link was accepted; the input only clears on success. */
  onAdd: (url: string) => boolean;
  placeholder: string;
};

export function AssetLinkInput({
  error,
  isWhitelabel,
  onAdd,
  placeholder,
}: AssetLinkInputProps) {
  const inputId = useId();
  const errorId = useId();
  const [value, setValue] = useState("");
  const shareTarget = isWhitelabel ? null : getShareTarget(value.trim());

  const add = () => {
    const url = value.trim();

    if (url && onAdd(url)) setValue("");
  };

  return (
    <div>
      <label
        className="mb-md block text-sm font-medium text-text-primary"
        htmlFor={inputId}
      >
        Or paste a link
      </label>
      <div className="flex gap-md">
        <div className="relative min-w-0 flex-1">
          <Link2 className="pointer-events-none absolute left-[13px] top-1/2 size-4 -translate-y-1/2 text-text-quaternary" />
          <input
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
            autoComplete="off"
            className="h-11 w-full rounded-[10px] border border-border-primary bg-bg-primary pl-[38px] pr-lg text-sm text-text-primary outline-hidden placeholder:text-text-quaternary focus:border-border-brand-solid focus:ring-2 focus:ring-fg-brand-primary aria-invalid:border-border-error-solid"
            id={inputId}
            inputMode="url"
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;

              event.preventDefault();
              add();
            }}
            placeholder={placeholder}
            type="text"
            value={value}
          />
        </div>
        <Button
          className="h-11 rounded-[10px] border border-border-primary bg-bg-primary px-[18px] text-sm font-semibold text-text-brand-primary outline-hidden transition-colors hover:border-border-brand-solid focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
          onPress={add}
          variant="unstyled"
        >
          Add link
        </Button>
      </div>
      {error ? (
        <p
          className="mt-sm text-xs text-text-error-primary"
          id={errorId}
          role="alert"
        >
          {error}
        </p>
      ) : null}
      {shareTarget ? (
        <p className="mt-md flex flex-wrap items-center gap-sm text-[13px] text-text-quaternary">
          <Lock className="size-3.5 shrink-0" />
          <span>
            Private folder? Share it with{" "}
            <b className="font-semibold text-text-primary">
              {shareTarget.email}
            </b>{" "}
            so your designer can open it.
          </span>
          <CopyEmailButton {...shareTarget} />
        </p>
      ) : null}
      {isWhitelabel ? (
        <p className="mt-md text-xs text-text-tertiary">
          When sharing links please provide a public link.
        </p>
      ) : null}
    </div>
  );
}
